/**
 * Pure fleet comparison helpers for the insight engine.
 * Handles the GP50 −999 / null sentinel convention.
 */

import type { BetterDirection, InsightRow } from './types';

/** Sentinel / missing values treated as "no measurement". */
export function isMissingValue(value: unknown): boolean {
  if (value == null) {return true;}
  if (typeof value === 'string' && value.trim() === '') {return true;}
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) {return true;}
  // −999 and its kph twin (−1850.15) plus a floor for older sentinels
  if (n <= -900) {return true;}
  return false;
}

export function toFiniteNumber(value: unknown): number | null {
  if (isMissingValue(value)) {return null;}
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Case-insensitive key lookup on an API row. */
export function getRowValue(row: InsightRow, key: string): unknown {
  if (key in row) {return row[key];}
  const lower = key.toLowerCase();
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === lower) {return row[k];}
  }
  return undefined;
}

export function getTeamName(row: InsightRow): string {
  const raw =
    getRowValue(row, 'TEAM') ??
    getRowValue(row, 'source_name') ??
    getRowValue(row, 'Source_name') ??
    getRowValue(row, 'team');
  return String(raw ?? '').trim();
}

export function findHomeRow(rows: InsightRow[], homeBoatName: string): InsightRow | null {
  const target = homeBoatName.trim().toLowerCase();
  if (!target) {return null;}
  for (const row of rows) {
    if (getTeamName(row).toLowerCase() === target) {return row;}
  }
  return null;
}

export function mean(values: number[]): number | null {
  if (values.length === 0) {return null;}
  return values.reduce((a, b) => a + b, 0) / values.length;
}

export function median(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const left = sorted[mid - 1];
  const right = sorted[mid];
  if (sorted.length % 2 === 0) {
    if (left == null || right == null) {
      return null;
    }
    return (left + right) / 2;
  }
  return right ?? null;
}

export interface RankedRow {
  row: InsightRow;
  team: string;
  value: number;
  rank: number;
}

/**
 * Rank rows by a numeric column. Missing values sort last.
 * `ascending` true = lower is better (time/position); false = higher is better (distance/speed).
 */
export function rankByColumn(
  rows: InsightRow[],
  key: string,
  ascending: boolean
): RankedRow[] {
  const withValues = rows.map((row) => ({
    row,
    team: getTeamName(row),
    value: toFiniteNumber(getRowValue(row, key)),
  }));

  const usable = withValues.filter((r): r is { row: InsightRow; team: string; value: number } => r.value != null);
  const missing = withValues.filter((r) => r.value == null);

  usable.sort((a, b) => (ascending ? a.value - b.value : b.value - a.value));

  const ranked: RankedRow[] = usable.map((r, i) => ({
    row: r.row,
    team: r.team,
    value: r.value,
    rank: i + 1,
  }));

  for (const m of missing) {
    ranked.push({
      row: m.row,
      team: m.team,
      value: Number.NaN,
      rank: ranked.length + 1,
    });
  }
  return ranked;
}

/**
 * Top-N boats by a ranking column, excluding the home boat.
 * Uses top-3 when fleet >= 6, otherwise top-2.
 */
export function leaders(
  rows: InsightRow[],
  key: string,
  homeBoatName: string,
  ascending: boolean
): RankedRow[] {
  const ranked = rankByColumn(rows, key, ascending);
  const home = homeBoatName.trim().toLowerCase();
  const withoutHome = ranked.filter(
    (r) => r.team.toLowerCase() !== home && Number.isFinite(r.value)
  );
  const n = rows.length >= 6 ? 3 : 2;
  return withoutHome.slice(0, n);
}

export function leadersMean(leaderRows: RankedRow[]): number | null {
  return mean(leaderRows.map((r) => r.value).filter((v) => Number.isFinite(v)));
}

export function fleetMedianForColumn(rows: InsightRow[], key: string): number | null {
  const values: number[] = [];
  for (const row of rows) {
    const v = toFiniteNumber(getRowValue(row, key));
    if (v != null) {values.push(v);}
  }
  return median(values);
}

export function meaningfulGap(
  ours: number | null,
  theirs: number | null,
  threshold: number,
  better: BetterDirection
): { gap: number; weAreWorse: boolean } | null {
  if (ours == null || theirs == null) {return null;}
  const gap = ours - theirs;
  const abs = Math.abs(gap);
  if (abs < threshold) {return null;}
  const weAreWorse =
    better === 'higher' ? ours < theirs : ours > theirs;
  return { gap, weAreWorse };
}

/** How many leaders to request given fleet size. */
export function leaderCountForFleet(fleetSize: number): number {
  return fleetSize >= 6 ? 3 : 2;
}

export function formatNumber(value: number | null, digits = 1): string {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  return value.toFixed(digits);
}

export function teamListLabel(teams: string[]): string {
  if (teams.length === 0) {
    return 'leaders';
  }
  const first = teams[0] ?? '';
  if (teams.length === 1) {
    return first;
  }
  if (teams.length === 2) {
    return `${first} and ${teams[1] ?? ''}`;
  }
  const last = teams[teams.length - 1] ?? '';
  return `${teams.slice(0, -1).join(', ')}, and ${last}`;
}
